import {
  PRINT_LOGO_BYTES_PER_ROW,
  PRINT_LOGO_MAX_HEIGHT_DOTS,
  PRINT_LOGO_WIDTH_DOTS,
  type PrintLogo,
} from '@gc/shared';

/**
 * Turn a picture into printer dots, here in the browser.
 *
 * A thermal printer takes one BIT per dot, black or white, packed eight to a
 * byte. Getting there means decode, scale to the paper width, convert to grey,
 * threshold, and pack — five steps the server cannot do without an image
 * library this repo will not add unasked, and which a canvas does natively.
 *
 * Done ONCE, when the operator uploads. Converting per receipt would put image
 * processing between a cashier pressing Settle and paper coming out.
 */

/**
 * Anything lighter than this becomes white.
 *
 * A thermal head has no grey: a dot is burned or it is not. Mid-grey is the
 * unhelpful default — most logos are dark artwork on a transparent or white
 * field, and a middling cut turns antialiased edges into speckle. Biased dark
 * so only genuinely dark pixels burn, which keeps edges clean at the cost of
 * losing the faintest tints, and a faint tint would not survive the paper
 * anyway.
 */
const BLACK_BELOW = 100;

/** Transparent pixels are paper, not black. */
const OPAQUE_ABOVE = 128;

/**
 * The pure half: RGBA pixels in, packed rows out.
 *
 * Split from the canvas work so the packing — the part with the bit arithmetic
 * and the off-by-ones — can be tested in plain node, which is all this
 * workspace's test runner offers.
 */
export function packToPrinterBits(
  rgba: Uint8ClampedArray,
  widthDots: number,
  heightDots: number,
): Uint8Array {
  const bytesPerRow = widthDots / 8;
  const packed = new Uint8Array(bytesPerRow * heightDots);

  for (let y = 0; y < heightDots; y += 1) {
    for (let x = 0; x < widthDots; x += 1) {
      const at = (y * widthDots + x) * 4;
      const alpha = rgba[at + 3] ?? 0;
      if (alpha < OPAQUE_ABOVE) continue;

      // Rec. 601 luma. Plain averaging turns a saturated red logo into
      // mid-grey and then, at this threshold, into nothing.
      const luma =
        0.299 * (rgba[at] ?? 0) + 0.587 * (rgba[at + 1] ?? 0) + 0.114 * (rgba[at + 2] ?? 0);
      if (luma >= BLACK_BELOW) continue;

      // A 1 BIT IS A BLACK DOT — the opposite of how a screen thinks about it,
      // and the way every ESC/POS raster command reads it. The most significant
      // bit of each byte is the leftmost dot.
      packed[y * bytesPerRow + (x >> 3)]! |= 0x80 >> (x & 7);
    }
  }

  return packed;
}

/** Height that fits the paper width without distorting the artwork. */
export function scaledHeight(naturalWidth: number, naturalHeight: number): number {
  if (naturalWidth <= 0 || naturalHeight <= 0) return 0;
  const scaled = Math.round((naturalHeight / naturalWidth) * PRINT_LOGO_WIDTH_DOTS);
  // A tall logo is capped rather than squashed: a squashed logo is a wrong
  // logo, where a cropped one is at least the venue's own artwork.
  return Math.max(1, Math.min(PRINT_LOGO_MAX_HEIGHT_DOTS, scaled));
}

/** Base64 without a data-URI prefix, chunked so a big logo cannot blow the stack. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 8192;
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
  }
  return btoa(binary);
}

/**
 * Read an image file and convert it. Browser-only — needs a canvas.
 *
 * Rejects rather than returning something half-right: a logo that failed to
 * decode must be reported to the operator uploading it, not stored as an empty
 * rectangle they discover on a customer's receipt.
 */
export async function convertLogoFile(file: File): Promise<PrintLogo> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    const heightDots = scaledHeight(image.naturalWidth, image.naturalHeight);
    if (heightDots === 0) throw new Error('That image has no dimensions.');

    const canvas = document.createElement('canvas');
    canvas.width = PRINT_LOGO_WIDTH_DOTS;
    canvas.height = heightDots;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser will not give us a canvas to convert with.');

    // White underneath: a PNG with transparency would otherwise read as black
    // and print a solid block the size of the logo.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    const packed = packToPrinterBits(data, PRINT_LOGO_WIDTH_DOTS, heightDots);
    if (packed.length !== heightDots * PRINT_LOGO_BYTES_PER_ROW) {
      throw new Error('The converted logo came out the wrong size.');
    }

    return { widthDots: PRINT_LOGO_WIDTH_DOTS, heightDots, data: toBase64(packed) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('That file could not be read as an image.'));
    image.src = url;
  });
}
